import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Button,
  Switch,
  Divider,
  Avatar,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
  Alert,
  SelectChangeEvent,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import SecurityIcon from '@mui/icons-material/Security';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PaletteIcon from '@mui/icons-material/Palette';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import PublicIcon from '@mui/icons-material/Public';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DownloadIcon from '@mui/icons-material/Download';
import DevicesIcon from '@mui/icons-material/Devices';
import LockIcon from '@mui/icons-material/Lock';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { API_BASE_URL, STORAGE_KEYS } from '../../constants';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface PasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface NotificationSettings {
  emailNotifications: boolean;
  pushNotifications: boolean;
  marketingEmails: boolean;
  applicationUpdates: boolean;
  paymentReminders: boolean;
}

interface DisplaySettings {
  theme: 'light' | 'auto';
  language: string;
  dateFormat: string;
  timezone: string;
}

interface PrivacySettings {
  shareAnonymousUsage: boolean;
  shareProfilePublicly: boolean;
  allowThirdPartyIntegrations: boolean;
}

interface ActiveSession {
  id: string;
  device: string;
  browser: string;
  ip: string;
  lastActive: string;
  current: boolean;
}

type TabKey = 'account' | 'security' | 'notifications' | 'display' | 'privacy';

const TAB_ITEMS: { key: TabKey; label: string; icon: React.ReactElement }[] = [
  { key: 'account', label: 'Account', icon: <PersonIcon /> },
  { key: 'security', label: 'Security', icon: <SecurityIcon /> },
  { key: 'notifications', label: 'Notifications', icon: <NotificationsIcon /> },
  { key: 'display', label: 'Display', icon: <PaletteIcon /> },
  { key: 'privacy', label: 'Data & Privacy', icon: <PrivacyTipIcon /> },
];

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const SETTINGS_PREFIX = 'tripnovac_settings_';

function loadSetting<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(`${SETTINGS_PREFIX}${key}`);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function saveSetting<T>(key: string, value: T): void {
  localStorage.setItem(`${SETTINGS_PREFIX}${key}`, JSON.stringify(value));
}

const COMMON_TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Moscow',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Australia/Sydney',
  'Pacific/Auckland',
];

const DATE_FORMAT_OPTIONS = [
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
  { value: 'MMM DD, YYYY', label: 'MMM DD, YYYY' },
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const SettingsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, token } = useAuth();
  const { showSuccess, showError } = useNotification();

  // Active tab
  const [activeTab, setActiveTab] = useState<TabKey>('account');

  // Account fields
  const [email, setEmail] = useState(user?.email ?? '');
  const [phone, setPhone] = useState(user?.phone ?? '');
  const [nationality, setNationality] = useState(user?.nationality ?? '');
  const [accountSaving, setAccountSaving] = useState(false);

  // Security
  const [passwordForm, setPasswordForm] = useState<PasswordForm>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [twoFAEnabled, setTwoFAEnabled] = useState<boolean>(
    loadSetting('2fa_enabled', false),
  );
  const [sessions] = useState<ActiveSession[]>([
    {
      id: '1',
      device: 'Windows PC',
      browser: 'Chrome 122',
      ip: '••••••••',
      lastActive: new Date().toISOString(),
      current: true,
    },
  ]);

  // Notifications
  const [notifSettings, setNotifSettings] = useState<NotificationSettings>(
    loadSetting<NotificationSettings>('notifications', {
      emailNotifications: true,
      pushNotifications: true,
      marketingEmails: false,
      applicationUpdates: true,
      paymentReminders: true,
    }),
  );

  // Display
  const [displaySettings, setDisplaySettings] = useState<DisplaySettings>(
    loadSetting<DisplaySettings>('display', {
      theme: 'light',
      language: 'en',
      dateFormat: 'MMM DD, YYYY',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    }),
  );

  // Privacy
  const [privacySettings, setPrivacySettings] = useState<PrivacySettings>(
    loadSetting<PrivacySettings>('privacy', {
      shareAnonymousUsage: true,
      shareProfilePublicly: false,
      allowThirdPartyIntegrations: false,
    }),
  );

  // Dialogs
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [downloadingData, setDownloadingData] = useState(false);

  // Sync user data when user prop updates
  useEffect(() => {
    if (user) {
      setEmail(user.email);
      setPhone(user.phone ?? '');
      setNationality(user.nationality ?? '');
    }
  }, [user]);

  // Persist notification, display, privacy settings on change
  useEffect(() => { saveSetting('notifications', notifSettings); }, [notifSettings]);
  useEffect(() => { saveSetting('display', displaySettings); }, [displaySettings]);
  useEffect(() => { saveSetting('privacy', privacySettings); }, [privacySettings]);
  useEffect(() => { saveSetting('2fa_enabled', twoFAEnabled); }, [twoFAEnabled]);

  /* ---- API helpers ---- */

  const authHeaders = useCallback(
    () => ({
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }),
    [token],
  );

  const handleAccountSave = async () => {
    setAccountSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/profile`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ email, phone, nationality }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || 'Failed to update account');
      }
      showSuccess('Account details updated successfully.');
    } catch (err: any) {
      showError(err.message || 'Failed to update account details.');
    } finally {
      setAccountSaving(false);
    }
  };

  const handleChangePassword = async () => {
    const { currentPassword, newPassword, confirmPassword } = passwordForm;
    if (!currentPassword || !newPassword || !confirmPassword) {
      showError('Please fill all password fields.');
      return;
    }
    if (newPassword.length < 8) {
      showError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      showError('Passwords do not match.');
      return;
    }
    setPasswordSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/users/change-password`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ currentPassword, newPassword, confirmPassword }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || 'Password change failed');
      }
      showSuccess('Password changed successfully.');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err: any) {
      showError(err.message || 'Failed to change password.');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleDeactivateAccount = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/auth/deactivate`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Deactivation failed');
      showSuccess('Account deactivated. You can reactivate by logging in again.');
      setDeactivateDialogOpen(false);
    } catch {
      showError('Failed to deactivate account.');
    }
  };

  const handleDownloadData = async () => {
    setDownloadingData(true);
    try {
      const res = await fetch(`${API_BASE_URL}/compliance/export-data`, {
        method: 'GET',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tripnovac_data_export_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showSuccess('Your data has been downloaded.');
    } catch {
      showError('Failed to download your data. Please try again later.');
    } finally {
      setDownloadingData(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      showError('Please type DELETE to confirm.');
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/auth/delete-account`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Deletion failed');
      showSuccess('Your account has been permanently deleted.');
      setDeleteDialogOpen(false);
      // Auth context should redirect after user/token are cleared
      localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);
      window.location.href = '/';
    } catch {
      showError('Failed to delete account. Please contact support.');
    }
  };

  /* ---- Notification toggle helper ---- */
  const toggleNotif = (key: keyof NotificationSettings) => {
    setNotifSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  /* ---- Display change helpers ---- */
  const changeDisplay = (field: keyof DisplaySettings, value: string) => {
    setDisplaySettings((prev) => ({ ...prev, [field]: value }));
  };

  /* ---- Privacy toggle helper ---- */
  const togglePrivacy = (key: keyof PrivacySettings) => {
    setPrivacySettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  /* ================================================================== */
  /*  Render Panels                                                      */
  /* ================================================================== */

  const renderAccount = () => (
    <Box>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        Account Information
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage your personal details and account preferences.
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Avatar
          sx={{
            width: 72,
            height: 72,
            bgcolor: 'primary.main',
            fontSize: 28,
            fontWeight: 700,
          }}
        >
          {user?.firstName?.charAt(0) ?? user?.email?.charAt(0)?.toUpperCase() ?? 'U'}
        </Avatar>
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            {user?.firstName} {user?.lastName}
          </Typography>
          <Chip
            label={user?.role?.toUpperCase() ?? 'USER'}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 520 }}>
        <TextField
          label="Email Address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          type="email"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Phone Number"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PhoneIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Nationality"
          value={nationality}
          onChange={(e) => setNationality(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PublicIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
          <Button
            variant="contained"
            onClick={handleAccountSave}
            disabled={accountSaving}
            startIcon={accountSaving ? <CircularProgress size={18} /> : <CheckCircleIcon />}
          >
            {accountSaving ? 'Saving…' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      <Divider sx={{ my: 5 }} />

      {/* Deactivate */}
      <Typography variant="subtitle1" fontWeight={600} color="warning.main" gutterBottom>
        Deactivate Account
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Temporarily disable your account. You can reactivate it by logging in.
      </Typography>
      <Button
        variant="outlined"
        color="warning"
        startIcon={<WarningAmberIcon />}
        onClick={() => setDeactivateDialogOpen(true)}
      >
        Deactivate Account
      </Button>

      {/* Deactivate dialog */}
      <Dialog open={deactivateDialogOpen} onClose={() => setDeactivateDialogOpen(false)}>
        <DialogTitle>Deactivate your Tripnovac account?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your account will be disabled and your profile hidden. You can reactivate it at any
            time by signing in again.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateDialogOpen(false)}>Cancel</Button>
          <Button color="warning" variant="contained" onClick={handleDeactivateAccount}>
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  /* ---- Security ---- */
  const renderSecurity = () => (
    <Box>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        Security
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Keep your Tripnovac account safe with a strong password and two-factor authentication.
      </Typography>

      {/* Change password */}
      <Paper variant="outlined" sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          <LockIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
          Change Password
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 480 }}>
          <TextField
            label="Current Password"
            type={showCurrentPw ? 'text' : 'password'}
            value={passwordForm.currentPassword}
            onChange={(e) => setPasswordForm((p) => ({ ...p, currentPassword: e.target.value }))}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowCurrentPw((s) => !s)} edge="end">
                    {showCurrentPw ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="New Password"
            type={showNewPw ? 'text' : 'password'}
            value={passwordForm.newPassword}
            onChange={(e) => setPasswordForm((p) => ({ ...p, newPassword: e.target.value }))}
            fullWidth
            helperText="Minimum 8 characters"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowNewPw((s) => !s)} edge="end">
                    {showNewPw ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Confirm New Password"
            type={showConfirmPw ? 'text' : 'password'}
            value={passwordForm.confirmPassword}
            onChange={(e) => setPasswordForm((p) => ({ ...p, confirmPassword: e.target.value }))}
            fullWidth
            error={
              !!passwordForm.confirmPassword &&
              passwordForm.newPassword !== passwordForm.confirmPassword
            }
            helperText={
              passwordForm.confirmPassword &&
              passwordForm.newPassword !== passwordForm.confirmPassword
                ? 'Passwords do not match'
                : ''
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowConfirmPw((s) => !s)} edge="end">
                    {showConfirmPw ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            onClick={handleChangePassword}
            disabled={passwordSaving}
            sx={{ alignSelf: 'flex-start', mt: 0.5 }}
            startIcon={passwordSaving ? <CircularProgress size={18} /> : <LockIcon />}
          >
            {passwordSaving ? 'Updating…' : 'Update Password'}
          </Button>
        </Box>
      </Paper>

      {/* 2FA */}
      <Paper variant="outlined" sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Two-Factor Authentication
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add an extra layer of security to your account.
            </Typography>
          </Box>
          <Switch
            checked={twoFAEnabled}
            onChange={() => {
              setTwoFAEnabled((prev) => !prev);
              showSuccess(
                twoFAEnabled
                  ? 'Two-factor authentication disabled.'
                  : 'Two-factor authentication enabled.',
              );
            }}
            color="primary"
          />
        </Box>
        {twoFAEnabled && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Two-factor authentication is active. You'll be asked for a verification code on new
            sign-ins.
          </Alert>
        )}
      </Paper>

      {/* Active sessions */}
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          <DevicesIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
          Active Sessions
        </Typography>
        {sessions.map((s) => (
          <Box
            key={s.id}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              '&:last-child': { borderBottom: 'none' },
            }}
          >
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {s.device} — {s.browser}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                IP: {s.ip} · Last active:{' '}
                {new Date(s.lastActive).toLocaleString()}
              </Typography>
            </Box>
            {s.current ? (
              <Chip label="Current" color="success" size="small" variant="outlined" />
            ) : (
              <Button size="small" color="error">
                Revoke
              </Button>
            )}
          </Box>
        ))}
      </Paper>
    </Box>
  );

  /* ---- Notifications ---- */
  const renderNotifications = () => {
    const items: { key: keyof NotificationSettings; label: string; description: string }[] = [
      {
        key: 'emailNotifications',
        label: 'Email Notifications',
        description: 'Receive important updates and alerts via email.',
      },
      {
        key: 'pushNotifications',
        label: 'Push Notifications',
        description: 'Get instant push notifications on your devices.',
      },
      {
        key: 'marketingEmails',
        label: 'Marketing Emails',
        description: 'Receive promotions, offers, and Tripnovac news.',
      },
      {
        key: 'applicationUpdates',
        label: 'Application Updates',
        description: 'Be notified when your visa application status changes.',
      },
      {
        key: 'paymentReminders',
        label: 'Payment Reminders',
        description: 'Get reminders for upcoming and overdue payments.',
      },
    ];

    return (
      <Box>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          Notification Preferences
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Choose what notifications you'd like to receive from Tripnovac.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {items.map(({ key, label, description }) => (
            <Paper
              key={key}
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>
                  {label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {description}
                </Typography>
              </Box>
              <Switch
                checked={notifSettings[key]}
                onChange={() => toggleNotif(key)}
                color="primary"
              />
            </Paper>
          ))}
        </Box>
      </Box>
    );
  };

  /* ---- Display ---- */
  const renderDisplay = () => (
    <Box>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        Display Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Customize how Tripnovac looks and feels for you.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 480 }}>
        {/* Theme */}
        <FormControl fullWidth>
          <InputLabel>Theme Preference</InputLabel>
          <Select
            value={displaySettings.theme}
            label="Theme Preference"
            onChange={(e: SelectChangeEvent) => changeDisplay('theme', e.target.value)}
          >
            <MenuItem value="light">Light</MenuItem>
            <MenuItem value="auto">Auto (System)</MenuItem>
          </Select>
        </FormControl>

        {/* Language */}
        <FormControl fullWidth>
          <InputLabel>Language</InputLabel>
          <Select
            value={displaySettings.language}
            label="Language"
            onChange={(e: SelectChangeEvent) => changeDisplay('language', e.target.value)}
          >
            <MenuItem value="en">English</MenuItem>
          </Select>
        </FormControl>

        {/* Date format */}
        <FormControl fullWidth>
          <InputLabel>Date Format</InputLabel>
          <Select
            value={displaySettings.dateFormat}
            label="Date Format"
            onChange={(e: SelectChangeEvent) => changeDisplay('dateFormat', e.target.value)}
          >
            {DATE_FORMAT_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Timezone */}
        <FormControl fullWidth>
          <InputLabel>Timezone</InputLabel>
          <Select
            value={displaySettings.timezone}
            label="Timezone"
            onChange={(e: SelectChangeEvent) => changeDisplay('timezone', e.target.value)}
          >
            {COMMON_TIMEZONES.map((tz) => (
              <MenuItem key={tz} value={tz}>
                {tz.replace(/_/g, ' ')}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );

  /* ---- Data & Privacy ---- */
  const renderPrivacy = () => (
    <Box>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        Data &amp; Privacy
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Control your data and how Tripnovac uses it.
      </Typography>

      {/* Data sharing preferences */}
      <Paper variant="outlined" sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Data Sharing Preferences
        </Typography>

        {(
          [
            {
              key: 'shareAnonymousUsage' as keyof PrivacySettings,
              label: 'Anonymous Usage Data',
              desc: 'Help improve Tripnovac by sharing anonymous usage statistics.',
            },
            {
              key: 'shareProfilePublicly' as keyof PrivacySettings,
              label: 'Public Profile',
              desc: 'Allow other users to see your basic profile information.',
            },
            {
              key: 'allowThirdPartyIntegrations' as keyof PrivacySettings,
              label: 'Third-Party Integrations',
              desc: 'Allow Tripnovac to share limited data with partner services.',
            },
          ] as const
        ).map(({ key, label, desc }) => (
          <Box
            key={key}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              '&:last-child': { borderBottom: 'none' },
            }}
          >
            <Box>
              <Typography variant="subtitle2" fontWeight={600}>
                {label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {desc}
              </Typography>
            </Box>
            <Switch
              checked={privacySettings[key]}
              onChange={() => togglePrivacy(key)}
              color="primary"
            />
          </Box>
        ))}
      </Paper>

      {/* Download data */}
      <Paper variant="outlined" sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Download My Data
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Request a full export of all personal data Tripnovac stores about you.
        </Typography>
        <Button
          variant="outlined"
          startIcon={downloadingData ? <CircularProgress size={18} /> : <DownloadIcon />}
          onClick={handleDownloadData}
          disabled={downloadingData}
        >
          {downloadingData ? 'Preparing…' : 'Download My Data'}
        </Button>
      </Paper>

      {/* Delete account */}
      <Paper
        variant="outlined"
        sx={{ p: 3, borderRadius: 2, borderColor: 'error.main' }}
      >
        <Typography variant="subtitle1" fontWeight={600} color="error" gutterBottom>
          <DeleteForeverIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
          Delete Account
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Permanently remove your Tripnovac account, applications, and all associated data. This
          action cannot be undone.
        </Typography>
        <Button
          variant="contained"
          color="error"
          startIcon={<DeleteForeverIcon />}
          onClick={() => setDeleteDialogOpen(true)}
        >
          Delete My Account
        </Button>
      </Paper>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ color: 'error.main' }}>
          Permanently delete your Tripnovac account?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This will permanently erase all your data, including applications, documents, and
            payment history. This action <strong>cannot be undone</strong>.
          </DialogContentText>
          <TextField
            label='Type "DELETE" to confirm'
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            fullWidth
            autoFocus
            error={!!deleteConfirmText && deleteConfirmText !== 'DELETE'}
            helperText={
              deleteConfirmText && deleteConfirmText !== 'DELETE'
                ? 'Please type DELETE in all caps.'
                : ''
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setDeleteConfirmText('');
            }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteConfirmText !== 'DELETE'}
            onClick={handleDeleteAccount}
          >
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  /* ---- Panel router ---- */
  const renderPanel = () => {
    switch (activeTab) {
      case 'account':
        return renderAccount();
      case 'security':
        return renderSecurity();
      case 'notifications':
        return renderNotifications();
      case 'display':
        return renderDisplay();
      case 'privacy':
        return renderPrivacy();
      default:
        return null;
    }
  };

  /* ================================================================== */
  /*  Main render                                                        */
  /* ================================================================== */

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* ---- Hero ---- */}
      <Box
        sx={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=1920&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center 40%',
          color: '#fff',
          minHeight: 200,
          display: 'flex',
          alignItems: 'center',
          py: { xs: 5, md: 7 },
          px: { xs: 2, md: 6 },
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto', width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <SettingsIcon sx={{ fontSize: 32, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }} />
            <Typography variant="h4" fontWeight={800} letterSpacing={-0.5} sx={{ textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
              Settings
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ maxWidth: 600, textShadow: '0 1px 4px rgba(0,0,0,0.3)' }}>
            Manage your Tripnovac account, security preferences, notifications, and privacy
            controls from one place.
          </Typography>
        </Box>
      </Box>

      {/* ---- Content ---- */}
      <Box
        sx={{
          maxWidth: 1200,
          mx: 'auto',
          px: { xs: 2, md: 4 },
          py: { xs: 3, md: 5 },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3,
        }}
      >
        {/* Sidebar / Tabs */}
        {isMobile ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'auto',
            }}
          >
            <Tabs
              value={TAB_ITEMS.findIndex((t) => t.key === activeTab)}
              onChange={(_, idx) => setActiveTab(TAB_ITEMS[idx].key)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                minHeight: 48,
                '& .MuiTab-root': { minHeight: 48, textTransform: 'none', fontWeight: 600 },
              }}
            >
              {TAB_ITEMS.map((t) => (
                <Tab key={t.key} icon={t.icon} iconPosition="start" label={t.label} />
              ))}
            </Tabs>
          </Paper>
        ) : (
          <Paper
            elevation={0}
            sx={{
              width: 260,
              flexShrink: 0,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              alignSelf: 'flex-start',
              position: 'sticky',
              top: 24,
            }}
          >
            <List disablePadding>
              {TAB_ITEMS.map((t, idx) => (
                <React.Fragment key={t.key}>
                  <ListItemButton
                    selected={activeTab === t.key}
                    onClick={() => setActiveTab(t.key)}
                    sx={{
                      py: 1.5,
                      px: 2.5,
                      borderRadius: idx === 0 ? '8px 8px 0 0' : idx === TAB_ITEMS.length - 1 ? '0 0 8px 8px' : 0,
                      '&.Mui-selected': {
                        bgcolor: 'primary.50',
                        borderLeft: '3px solid',
                        borderColor: 'primary.main',
                        '& .MuiListItemIcon-root': { color: 'primary.main' },
                        '& .MuiListItemText-primary': { color: 'primary.main', fontWeight: 700 },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>{t.icon}</ListItemIcon>
                    <ListItemText
                      primary={t.label}
                      primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
                    />
                  </ListItemButton>
                  {idx < TAB_ITEMS.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        )}

        {/* Panel */}
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            p: { xs: 3, md: 4 },
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            minHeight: 480,
          }}
        >
          {renderPanel()}
        </Paper>
      </Box>
    </Box>
  );
};

export default SettingsPage;
